/**
 * Contents of this file were produced with a help of a code generation tool and
 * subsequently reviewed and edited by a human.
 *
 * While some of the code was created by an AI assisted service, manual adjustments
 * have been made to ensure correctness, readability, functionality and compliance
 * to Avalara's coding standards and guidelines. Any future modifications should
 * be approached with caution to avoid overwriting the manual changes.
 *
 * This source code is owned by Avalara Inc. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Proprietary and confidential.
 *
 * For more information, refer to the documentation or contact the development team.
 */

import { v4 as uuidv4 } from "uuid";
import { DebugLogger } from "../../utils/DebugLogger";

export interface CrossTabConfig {
    lockTimeout: number;
    minRefreshInterval: number;
}

export class CrossTabCoordinator {
    // eslint-disable-next-line no-use-before-define
    private static instance: CrossTabCoordinator | null = null;
    private config: CrossTabConfig;
    private readonly tabId: string;
    private static localStorageAvailable: boolean | null = null;

    private readonly KEYS = {
        REFRESH_LOCK: "ecm_sync_state",
        LAST_REFRESH: "ecm_sync_meta",
    } as const;

    private constructor(config: CrossTabConfig) {
        this.config = config;
        this.tabId = uuidv4();
    }

    private static isLocalStorageAvailable(): boolean {
        if (CrossTabCoordinator.localStorageAvailable !== null) {
            return CrossTabCoordinator.localStorageAvailable;
        }

        try {
            const testKey = "__ecm_storage_test__";
            localStorage.setItem(testKey, "test");
            localStorage.removeItem(testKey);
            CrossTabCoordinator.localStorageAvailable = true;
            return true;
        } catch {
            CrossTabCoordinator.localStorageAvailable = false;
            return false;
        }
    }

    public static getInstance(config?: CrossTabConfig): CrossTabCoordinator {
        if (!CrossTabCoordinator.instance && config) {
            CrossTabCoordinator.instance = new CrossTabCoordinator(config);
        }
        return CrossTabCoordinator.instance!;
    }

    /**
     * Attempts to acquire a lock for the specified key.
     *
     * This is the core coordination mechanism that prevents multiple tabs from
     * performing the same operation simultaneously. Uses localStorage as the
     * shared communication channel between tabs.
     *
     * Algorithm:
     * 1. Check if an existing lock exists and is still valid
     * 2. If valid lock exists, return false (another tab owns the operation)
     * 3. If no valid lock, create a new lock with current timestamp and tab ID
     * 4. Return true (this tab now owns the operation)
     *
     * @param key - The localStorage key to acquire a lock for
     * @returns true if lock was successfully acquired, false otherwise
     */
    private tryLock(key: string): boolean {
        if (!CrossTabCoordinator.isLocalStorageAvailable()) {
            return false;
        }

        try {
            const now = Date.now();

            // Step 1: Check for existing lock
            const existing = localStorage.getItem(key);
            DebugLogger.log(`Checking existing lock for ${key}`, existing, "CrossTab");

            if (existing) {
                try {
                    // Parse existing lock data (contains timestamp and tabId)
                    const lock = JSON.parse(existing) as { timestamp: number; tabId: string };
                    const lockAge = now - lock.timestamp;

                    DebugLogger.log(
                        `Found lock from tab ${lock.tabId?.substring(0, 8)}, age: ${lockAge}ms (timeout: ${this.config.lockTimeout}ms)`,
                        undefined,
                        "CrossTab"
                    );

                    // Step 2: Validate lock age against configured timeout
                    if (lockAge < this.config.lockTimeout) {
                        // Lock is still valid - another tab owns this operation
                        DebugLogger.log("Lock still valid, cannot acquire", undefined, "CrossTab");
                        return false;
                    }
                    // If we reach here, the lock has expired and can be overwritten
                    DebugLogger.log("Lock expired, can overwrite", undefined, "CrossTab");
                } catch (error) {
                    // Corrupted lock data - treat as expired and proceed to acquire
                    DebugLogger.log("Corrupted lock data, treating as expired", error, "CrossTab");
                }
            } else {
                DebugLogger.log("No existing lock found", undefined, "CrossTab");
            }

            // Step 3: Acquire the lock (pre-computed JSON string for performance)
            const lockData = `{"timestamp":${now},"tabId":"${this.tabId}"}`;
            localStorage.setItem(key, lockData);
            DebugLogger.log(
                `Lock acquired by tab ${this.tabId.substring(0, 8)}`,
                undefined,
                "CrossTab"
            );

            // Step 4: Successfully acquired the lock
            return true;
        } catch (error) {
            DebugLogger.log("Error during lock acquisition", error, "CrossTab");
            // Step 5: Handle any localStorage errors gracefully
            // This could happen due to:
            // - localStorage quota exceeded
            // - localStorage disabled (private browsing)
            // - JSON parsing errors from corrupted data
            // - Security restrictions
            return false;
        }
    }

    /**
     * Releases a previously acquired lock by removing it from localStorage.
     *
     * @param key - The localStorage key to release
     */
    private releaseLock(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch {
            // Ignore all errors during lock release
            // Possible errors:
            // - localStorage access denied
            // - Storage quota issues
            // - Browser security restrictions
        }
    }

    /**
     * Determines if this tab should handle session refresh operations.
     *
     * Algorithm:
     * 1. Check if any tab has successfully refreshed recently
     * 2. If recent refresh exists within minRefreshInterval, skip refresh (unless bypassed)
     * 3. If no recent refresh, attempt to acquire refresh lock
     * 4. Return true only if lock acquisition succeeds
     *
     * @param bypassMinInterval - If true, bypasses the minimum interval check (for server-requested immediate refreshes)
     * @returns true if this tab should handle the session refresh, false otherwise
     */
    public shouldRefresh(bypassMinInterval: boolean = false): boolean {
        // Production hardening: If localStorage is unavailable, allow refresh
        // This ensures the app continues working even without coordination
        if (!CrossTabCoordinator.isLocalStorageAvailable()) {
            DebugLogger.log("localStorage unavailable, allowing refresh", undefined, "CrossTab");
            return true;
        }

        // Step 1: Check for recent successful refresh by any tab
        try {
            const lastRefresh = localStorage.getItem(this.KEYS.LAST_REFRESH);
            if (lastRefresh) {
                const refresh = JSON.parse(lastRefresh) as { timestamp: number; tabId: string };
                const timeSinceRefresh = Date.now() - refresh.timestamp;

                DebugLogger.log(
                    `Last refresh: ${timeSinceRefresh}ms ago (min interval: ${this.config.minRefreshInterval}ms)`,
                    undefined,
                    "CrossTab"
                );

                // If another tab refreshed recently, skip this refresh attempt (unless bypassed)
                if (timeSinceRefresh < this.config.minRefreshInterval && !bypassMinInterval) {
                    DebugLogger.log("Recent refresh found, skipping", undefined, "CrossTab");
                    return false; // Recent refresh exists - no need to refresh again
                } else if (bypassMinInterval) {
                    DebugLogger.log(
                        "Recent refresh found but bypassing minimum interval (server-requested immediate refresh)",
                        undefined,
                        "CrossTab"
                    );
                }
                // If we reach here, the last refresh was long enough ago to allow a new one
                DebugLogger.log(
                    "Last refresh old enough, proceeding to lock acquisition",
                    undefined,
                    "CrossTab"
                );
            } else {
                DebugLogger.log(
                    "No previous refresh found, proceeding to lock acquisition",
                    undefined,
                    "CrossTab"
                );
            }
            // If no lastRefresh data exists, proceed to lock acquisition
        } catch (error) {
            DebugLogger.log("Error reading last refresh data", error, "CrossTab");
            // If we can't read the last refresh data (corrupted JSON, localStorage issues),
            // continue to lock acquisition rather than blocking the refresh entirely.
            // This ensures the system remains functional even with storage issues.
        }

        // Step 2: Attempt to acquire the refresh lock
        // This prevents multiple tabs from refreshing simultaneously
        const lockAcquired = this.tryLock(this.KEYS.REFRESH_LOCK);
        DebugLogger.log(
            `Lock acquisition ${lockAcquired ? "SUCCESS" : "FAILED"} for tab ${this.tabId.substring(0, 8)}`,
            undefined,
            "CrossTab"
        );
        return lockAcquired;
    }

    /**
     * Releases the session refresh lock.
     */
    public releaseRefreshLock(): void {
        DebugLogger.log(
            `Releasing refresh lock for tab ${this.tabId.substring(0, 8)}`,
            undefined,
            "CrossTab"
        );
        this.releaseLock(this.KEYS.REFRESH_LOCK);
    }

    /**
     * Records a successful session refresh for coordination with other tabs.
     *
     * This creates a timestamp that other tabs can check to avoid duplicate
     * refresh attempts. The timestamp is used by shouldRefresh() to implement
     * the minRefreshInterval coordination.
     */
    public recordSuccessfulRefresh(): void {
        // Production hardening: Early exit if localStorage is unavailable
        if (!CrossTabCoordinator.isLocalStorageAvailable()) {
            return;
        }

        try {
            // Pre-compute JSON string to avoid object creation
            const now = Date.now();
            const refreshData = `{"timestamp":${now},"tabId":"${this.tabId}"}`;
            localStorage.setItem(this.KEYS.LAST_REFRESH, refreshData);
            DebugLogger.log(
                `Recorded successful refresh for tab ${this.tabId.substring(0, 8)} at ${new Date(now).toISOString()}`,
                undefined,
                "CrossTab"
            );
        } catch (error) {
            DebugLogger.log("Error recording successful refresh", error, "CrossTab");
            // Ignore storage errors during success recording
            // Possible errors:
            // - localStorage quota exceeded
            // - localStorage disabled
            // - Security restrictions
        }
    }

    /**
     * Cleans up all cross-tab coordination data from localStorage.
     *
     * Removes both coordination keys:
     * - REFRESH_LOCK: Active session refresh lock
     * - LAST_REFRESH: Last successful refresh timestamp
     */
    public cleanup(): void {
        CrossTabCoordinator.cleanupCoordinationKeys();
    }

    /**
     * Static method to clean up cross-tab coordination keys without requiring an instance.
     */
    public static cleanupCoordinationKeys(): void {
        const CLEANUP_KEYS = ["ecm_sync_state", "ecm_sync_meta"] as const;

        CLEANUP_KEYS.forEach(key => {
            try {
                localStorage.removeItem(key);
            } catch {
                // Ignore individual key cleanup errors - cleanup should never fail the logout/close process
                // This handles scenarios like:
                // - localStorage disabled (private browsing)
                // - Storage quota exceeded
                // - Security restrictions
            }
        });
    }
}

export const DEFAULT_CROSS_TAB_CONFIG: CrossTabConfig = {
    /**
     * Lock timeout for session refresh operations.
     * This determines how long a refresh lock is considered valid before it expires.
     */
    lockTimeout: 15000,

    /**
     * Minimum interval between session refresh attempts across all tabs.
     * Prevents excessive API calls when multiple tabs detect session expiry
     * simultaneously. Once any tab successfully refreshes the session, other
     * tabs will skip their refresh attempts if within this interval.
     */
    minRefreshInterval: 30000,
};
